import React, { useCallback, useEffect, useState, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { LassieContext } from "../../../context/LassieProvider";
import LightningStrikeList from "../LightningStrikeList";
import RiskTimelineGraph from "../../lightning/lightning-windfarm/RiskTimelineGraph";
import { getInspectionDate } from "../../../prettyDate";
import {
  lassieDashboardAPI,
  lightningAPI,
  documentAPI,
  inspectionAPI,
  defectAPI
} from "../../../api";
import TurbineAnimation from "../../../components/turbine-animation/TurbineAnimation";
import Card from "../../../components/card/Card";
import ArrowAnimation from "../../../components/arrow-animation/ArrowAnimation";
import AlertPopup from '../../../components/alert-popup/AlertPopup';
import TurbineMap from "../../../components/turbine-map/TurbineMap";
import InspectionOverview from "../../../components/inspection-overview/InspectionOverview";
import UserActionsOverview from "../../../components/user-actions-overview/UserActionsOverview";
import DefectsOverview from "../../../components/defects-overview/DefectsOverview";
import turbineIcon from '../../../assets/img/windfarm.png'
import "./lightning-turbine-page.css";

export default function LightningTurbinePage() {
  const { fetchTurbines, turbines, turbinesLoading, refetchUserActionsFlag, setSelectedTurbine} =
    useContext(LassieContext);
  const [historicUserActions, setHistoricUserActions] = useState([]);
  const [riskTimeline, setRiskTimeline] = useState(null);
  const [showRiskGraph, setShowRiskGraph] = useState(false);
  const [loading, setLoading] = useState(true);
  const [documentLoading, setDocumentLoading] = useState({ id: null, status: false });
  const [inspections, setInspections] = useState([])
  const [editMode, setEditMode] = useState({ id: null, status: false });
  const [inspectionLoading, setInspectionLoading] = useState(false)
  const [turbineDefects, setTurbineDefects] = useState([])
  const [turbinePopup, setTurbinePopup] = useState({
    show: false,
    status: '',
    title: '',
    buttonText: '',
    buttonStatus: '',
    text: '',
    action: null,
    buttonText2: '',
    buttonStatus2: '',
    action2: null,
  })

  const datePickerRef = useRef();

  let { turbine_id } = useParams();
  turbine_id = parseInt(turbine_id);

  let { windfarm_id } = useParams();
  if (windfarm_id) {
    windfarm_id = parseInt(windfarm_id);
  }

  const turbine = turbines.length > 0 ? turbines[0] : null;

  useEffect(() => {
    refetchTurbineData(turbine_id);
    fetchDefects(turbine_id)
  }, [turbine_id]);


  useEffect(() => {
    if (refetchUserActionsFlag === 0) return;
    refetchTurbineData(turbine_id);
    fetchDefects(turbine_id)
    setSelectedTurbine(null)
  }, [refetchUserActionsFlag]);


  const fetchUserActions = useCallback(async () => {
    setLoading(true);
    try {
      const actions = await lassieDashboardAPI.listTurbineHistoricUserAction(turbine_id);
      setHistoricUserActions(actions);
    } catch (error) {
      console.error(
        `There was an error fetching turbine historic user action: ${error}`
      );
    } finally {
      setLoading(false);
    }
  }, [turbine_id]);


  const fetchDefects = useCallback(async () => {
    setLoading(true);
    try {
      const defects = await defectAPI.getTurbineDefects(turbine_id);
      setTurbineDefects(defects);
    } catch (error) {
      console.error(
        `There was an error fetching turbine defects: ${error}`
      );
    } finally {
      setLoading(false);
    }
  }, [turbine_id]);

  const downloadAttachment = useCallback(async (id) => {
    setDocumentLoading({ id: id, status: true });
    try {
      const url = await documentAPI.download(id);
      window.open(url, "_blank");
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDocumentLoading({ id: null, status: false });
    }
  }, []);

  const fetchInspections = useCallback(async () => {
    setLoading(true)
    try {
      const res = await inspectionAPI.listInspections(turbine_id)
      setInspections(res)
    } catch (error) {
      console.error(`There was an error fetching inspections: ${error}`)
    }
  })

  const callRiskData = async () => {
    setShowRiskGraph(!showRiskGraph);

    if (!riskTimeline || riskTimeline.length === 0) {
      try {
        const data = await lightningAPI.listWindfarmRisktimeline(windfarm_id);
        setRiskTimeline(data);
      } catch (error) {
        console.log(`There was an error fetching risk timeline: ${error}`);
      }
    }
  };

  const updateInspection = async (inspection_id, windfarm_id, inspection_date, inspection_type, inspection_method, inspection_status, inspection_reason) => {
    setInspectionLoading(true)
    try {
      const updatedInspection = await inspectionAPI.updateInspection(inspection_id, windfarm_id, inspection_date, inspection_type, inspection_method, inspection_status, inspection_reason);
      if (updatedInspection) {
        refetchTurbineData(turbine_id);


      }
    } catch (error) {
      console.log(`Error updating inspection: ${error}`);
      setTurbinePopup({
        show: true,
        status: 'wrong',
        title: 'Something went wrong',
        buttonText: 'Close',
        buttonStatus: 'wrong',
        text: error.response?.data
      });

    } finally {
      setInspectionLoading(false)
      setEditMode({ id: null, status: false });
    }
  }

  const openDatePicker = useCallback(() => {
    if (!datePickerRef.current) return;
    datePickerRef.current.showPicker();
  }, [datePickerRef]);


  const refetchTurbineData = (turbine_id) => {
    fetchTurbines(null, turbine_id);
    fetchInspections(turbine_id);
    fetchUserActions();
  }

  return (
    <div className="lightning-turbine-page">
      {!loading && !turbinesLoading && !inspectionLoading ? (
        <>
          <div className="header-container">
            <img src={turbineIcon} className="turbine-header-icon" />
            <h1>{turbine.name}</h1>
          </div>
          {turbine?.inspection?.due_date && ['pending', 'scheduled', 'overdue'].includes(turbine?.inspection?.status) &&
            (
              <div className="inspection-data-wrapper">
                {turbine?.inspection?.method && <p>
                  {turbine.inspection?.method} inspection
                </p>}
                <p>
                  {!turbine?.inspection?.method ? "Inspection " : null}
                  {getInspectionDate(
                    new Date(turbine.inspection.due_date)
                  )}
                </p>
              </div>
            )}

          <TurbineMap turbine={turbine} />

          <InspectionOverview inspections={inspections}
            updateInspection={updateInspection}
            editMode={editMode}
            setEditMode={setEditMode}
            datePickerRef={datePickerRef}
            openDatePicker={openDatePicker}
            downloadAttachment={downloadAttachment}
            documentLoading={documentLoading}
            turbine={turbine}
            setTurbinePopup={setTurbinePopup}
            refetchTurbineData={refetchTurbineData}
          />

          <DefectsOverview defects={turbineDefects} />

          <UserActionsOverview historicUserActions={historicUserActions}
            turbine={turbine}
            downloadAttachment={downloadAttachment}
            documentLoading={documentLoading}
            fetchUserActions={fetchUserActions}
            setTurbinePopup={setTurbinePopup}
            fetchInspections={fetchInspections}
          />

          {turbine.turbine_lightning.length > 0 && (
            <>
              <h2>Lightning activity</h2>
              <LightningStrikeList
                lightningStrikes={turbine.turbine_lightning}
              />
            </>
          )}

          <Card
            className={`risk-graph-area ${showRiskGraph ? "open" : "closed"}`}
          >
            <div className="risk-graph-header">
              <h2>Your risk timeline</h2>
              <ArrowAnimation
                showMore={showRiskGraph}
                setShowMore={() => callRiskData()}
                size={2}
              />
            </div>
            {showRiskGraph && turbine && (
              <RiskTimelineGraph
                riskTimeline={riskTimeline}
                turbine={[turbine]}
              />
            )}
          </Card>
        </>
      ) : (
        <TurbineAnimation />
      )}

      <AlertPopup status={turbinePopup.status}
        title={turbinePopup.title}
        showPopup={turbinePopup.show}
        closePopup={() => setTurbinePopup({ show: false })}
        buttonText={turbinePopup.buttonText}
        buttonStatus={turbinePopup.buttonStatus}
        action={turbinePopup.action}
        buttonText2={turbinePopup.buttonText2}
        buttonStatus2={turbinePopup.buttonStatus2}
        action2={turbinePopup.action2}
      >
        {turbinePopup.text}
      </AlertPopup>
    </div>
  );
}
